import { useCallback, useEffect, useRef } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryKey,
} from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { HTTPError } from 'ky';

import { api } from 'lib/request/api';
import { tcEffectiveWorkspaceIdAtom } from 'components/type-curve-workspace/atoms';
import type {
  SavedWellFilter,
  SavedWellFilterUpdateBody,
} from 'types/type-curve-workspace-show';
import type { TcWellFilters } from '../types';
import { TC_WELL_FILTER_CATEGORIES } from './utils';
import {
  tcFiltersLocalOnlyAtom,
  tcSavedWellFiltersAtom,
  tcWellFiltersAtom,
} from './atoms';
import {
  categoryDefinition,
  rowsToFlat,
  sliceForCategory,
} from './saved-well-filter-definition';

export const SAVED_WELL_FILTERS_KEY = 'tc-saved-well-filters';

// ky `api` has a prefixUrl, so paths must NOT start with a leading slash.
const savedFiltersPath = (workspaceId: number) =>
  `type_curve_workspaces/${workspaceId}/header/filters`;
const savedFilterItemPath = (workspaceId: number, filterId: number) =>
  `${savedFiltersPath(workspaceId)}/${filterId}`;

const savedFiltersKey = (workspaceId: number | null): QueryKey => [
  SAVED_WELL_FILTERS_KEY,
  workspaceId,
];

// Best-effort human message from a filters CRUD error. The backend renders
// validation failures as an array of `{ code, message, details }`; fall back to
// ky's message otherwise.
export async function parseFilterError(error: unknown): Promise<string | null> {
  if (!(error instanceof HTTPError)) {
    return error instanceof Error ? error.message : null;
  }
  const body = await error.response.json().catch(() => null);
  if (Array.isArray(body) && body[0]?.message) return String(body[0].message);
  if (body && typeof body === 'object') {
    const obj = body as { message?: string; error?: { message?: string } };
    if (obj.message) return obj.message;
    if (obj.error?.message) return obj.error.message;
  }
  return error.message;
}

// Loads the workspace's saved wells filters and mirrors them into
// tcSavedWellFiltersAtom (the category→id source + evidence source). A promoted
// workspace always exists, so the id is always set.
export function useSavedWellFiltersQuery() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const setSaved = useSetAtom(tcSavedWellFiltersAtom);

  const query = useQuery<SavedWellFilter[]>({
    queryKey: savedFiltersKey(workspaceId),
    queryFn: () =>
      api
        .get(savedFiltersPath(workspaceId as number))
        .json<{ filters: SavedWellFilter[] }>()
        .then((r) => r.filters),
    enabled: !!workspaceId,
  });

  const { data } = query;
  useEffect(() => {
    setSaved(workspaceId && data ? data : []);
  }, [data, workspaceId, setSaved]);

  return query;
}

// ─── Thin, non-optimistic CRUD mutations ───
// Standard pattern: fire → await → invalidate. No onMutate/rollback/temp ids.

function useInvalidateFilters() {
  const queryClient = useQueryClient();
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  return useCallback(
    () =>
      void queryClient.invalidateQueries({
        queryKey: savedFiltersKey(workspaceId),
      }),
    [queryClient, workspaceId]
  );
}

export function useCreateWellFilter() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<
    SavedWellFilter,
    unknown,
    { name: string; definition: Record<string, unknown> }
  >({
    mutationFn: ({ name, definition }) =>
      api
        // wrap_parameters :filter wraps these bare fields under `filter`.
        .post(savedFiltersPath(workspaceId as number), {
          json: { name, disabled: false, kind: 'filter', definition },
        })
        .json<SavedWellFilter>(),
    onSettled: invalidate,
  });
}

export function useUpdateWellFilter() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<
    SavedWellFilter,
    unknown,
    { filterId: number; body: SavedWellFilterUpdateBody }
  >({
    mutationFn: ({ filterId, body }) =>
      api
        .patch(savedFilterItemPath(workspaceId as number, filterId), {
          json: body,
        })
        .json<SavedWellFilter>(),
    onSettled: invalidate,
  });
}

export function useDeleteWellFilter() {
  const workspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const invalidate = useInvalidateFilters();
  return useMutation<unknown, unknown, { filterId: number }>({
    mutationFn: ({ filterId }) =>
      api.delete(savedFilterItemPath(workspaceId as number, filterId)),
    onSettled: invalidate,
  });
}

// ─── Per-property sync (batch, await, then reconcile) ───
// Diffs the applied filters per category and fires the batch of POST (new) /
// PATCH (changed) / DELETE (cleared) calls, awaiting all before returning. The
// category→id map comes from tcSavedWellFiltersAtom, keyed by `name` = category id.

export function useWellFilterSync() {
  const localOnly = useAtomValue(tcFiltersLocalOnlyAtom);
  const saved = useAtomValue(tcSavedWellFiltersAtom);
  const createFilter = useCreateWellFilter();
  const updateFilter = useUpdateWellFilter();
  const deleteFilter = useDeleteWellFilter();

  const idForCategory = useCallback(
    (categoryId: string): number | undefined => {
      const row = saved.find((f) => f.name === categoryId && f.id > 0);
      return row?.id;
    },
    [saved]
  );

  const removeCategory = useCallback(
    async (categoryId: string) => {
      // View-only page: a published workspace, or the Inventory Workspace.
      if (localOnly) return;
      const id = idForCategory(categoryId);
      if (id) await deleteFilter.mutateAsync({ filterId: id });
    },
    [localOnly, idForCategory, deleteFilter]
  );

  const syncFilters = useCallback(
    async (next: TcWellFilters, prev: TcWellFilters) => {
      // View-only page: skip persistence, caller still commits the atom.
      if (localOnly) return;
      const ops: Promise<unknown>[] = [];
      for (const cat of TC_WELL_FILTER_CATEGORIES) {
        const nextSlice = sliceForCategory(cat, next);
        const prevSlice = sliceForCategory(cat, prev);
        if (JSON.stringify(nextSlice) === JSON.stringify(prevSlice)) continue;

        const id = idForCategory(cat.id);
        const nextHasValue = Object.keys(nextSlice).length > 0;
        if (nextHasValue) {
          const definition = categoryDefinition(cat, next);
          ops.push(
            id
              ? updateFilter.mutateAsync({ filterId: id, body: { definition } })
              : createFilter.mutateAsync({ name: cat.id, definition })
          );
        } else if (id) {
          ops.push(deleteFilter.mutateAsync({ filterId: id }));
        }
      }
      await Promise.all(ops);
    },
    [localOnly, idForCategory, createFilter, updateFilter, deleteFilter]
  );

  const isSaving =
    createFilter.isPending || updateFilter.isPending || deleteFilter.isPending;

  return { syncFilters, removeCategory, isSaving };
}

// Runs the filters query and, once on first load, reconciles the applied filters:
// hydrate `tcWellFiltersAtom` from saved rows, or (when none exist yet) persist
// the client-seeded default filters as rows. Mount once per page.
export function useWellFiltersInit() {
  const query = useSavedWellFiltersQuery();
  const { syncFilters } = useWellFilterSync();
  const setWellFilters = useSetAtom(tcWellFiltersAtom);
  const [appliedFilters] = useAtom(tcWellFiltersAtom);
  const appliedRef = useRef(appliedFilters);
  appliedRef.current = appliedFilters;
  const initedRef = useRef(false);

  const { data, isSuccess } = query;

  // One-time initialization (hydrate from saved rows or persist defaults)
  useEffect(() => {
    if (initedRef.current || !isSuccess || !data) return;
    initedRef.current = true;
    if (data.length > 0) {
      setWellFilters(rowsToFlat(data));
    } else {
      // No saved rows yet — persist the currently-seeded default filters.
      void syncFilters(appliedRef.current, {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data]);

  // After initialization, re-hydrate when the query returns updated data
  // (e.g., backend created a development-type filter after an area change).
  const lastFilterJsonRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initedRef.current || !isSuccess || !data) return;
    const nextFilters = rowsToFlat(data);
    const nextJson = JSON.stringify(nextFilters);
    // First run after init — capture baseline, don't re-apply (already handled above)
    if (lastFilterJsonRef.current === undefined) {
      lastFilterJsonRef.current = nextJson;
      return;
    }
    // Only re-hydrate when filter-kind rows actually changed — cross-plot rows
    // share the same query key and their mutations should not cascade into a
    // wells refetch.
    if (nextJson !== lastFilterJsonRef.current) {
      lastFilterJsonRef.current = nextJson;
      setWellFilters(nextFilters);
    }
  }, [data, isSuccess, setWellFilters]);

  return query;
}
